import { useCallback, useContext, useEffect, useReducer } from 'react'
import {
    getProvisionalBoats,
    getUserRegistration,
    getUsersRegistrationWithLimiter,
} from '../../../../apiRoutes'
import {
    useAuth,
    usePermissions,
} from '../../../../context/AuthenticationContext'
import { ToastMessage } from '../../../../context/ToastPopUpContext'
import { useGlobalState } from '../../../../context/GlobalStateContext'
import { columnMapper, useDebounce } from '../../../../utilities/functionTools'
import {
    Grid,
    UniversalDataTable,
    UniversalLoadingCircle,
    UniversalModal,
} from '../../../UniversalComponents'
import UserRegistrationForm from './UserRegistrationForm'
import ContactMailIcon from '@mui/icons-material/ContactMail'
import UniversalToolBar from '../../../UniversalComponents/UniversalToolbar'
import { sendWelcomeEmails } from '../../../../apiRoutes/userRegistrationRoute'

const today = new Date().toISOString().substring(0, 10)

export const UserRegistration = () => {
    const setToastMessage = useContext(ToastMessage)
    const authentication = useAuth()
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer, userEmail } = oupUser
    const { auth } = authentication
    const { userPreferences } = auth
    const { setUser } = useAuth()

    const defaultState = {
        open: false,
        row: {},
        data: [],
        selectedUserIds: [],
        loading: false,
        paginationModel: { page: 0, pageSize: 50 },
        apiSearch: '',
        rowCount: 0,
        noResults: false,
        users: {
            userRegistration: {
                columns: [
                    {
                        field: 'userEmail',
                        headerName: 'EMAIL',
                        width: 300,
                        type: 'string',
                    },
                    {
                        field: 'lastEmailSent',
                        headerName: 'LAST EMAIL SENT',
                        width: 200,
                        type: 'dateTime',
                        valueGetter: (value) => {
                            return value ? new Date(value) : null
                        },
                    },
                    {
                        field: 'emailCount',
                        headerName: 'EMAILS SENT',
                        width: 150,
                        type: 'number',
                        headerAlign: 'center',
                        align: 'center',
                    },
                    {
                        field: 'userFirstName',
                        headerName: 'FIRST NAME',
                        width: 300,
                        type: 'string',
                    },
                    {
                        field: 'userLastName',
                        headerName: 'LAST NAME',
                        width: 300,
                        type: 'string',
                    },

                    {
                        field: 'fullHin',
                        headerName: 'FULLHIN',
                        width: 300,
                        type: 'string',
                    },
                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURER',
                        width: 300,
                        type: 'string',
                    },
                    {
                        field: 'userPhoneNumber',
                        headerName: 'Phone Number',
                        width: 300,
                        type: 'string',
                    },
                ],
                density: 'standard',
            },
        },
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const userPermissions = usePermissions()
    const {
        open,
        row,
        data,
        density,
        users,
        selectedUserIds,
        loading,
        paginationModel,
        apiSearch,
        rowCount,
        noResults,
    } = state
    const { userRegistration } = users

    const defaultColumns = [
        'userEmail',
        'userInviteSent.count',
        'userInviteSent.updatedAt',
        'lastEmailSent',
        'emailCount',
        'userFirstName',
        'userLastName',
        'userAddress',
        'userPhoneNumber',
        'boat.fullHin',
        'manufacturer.manufacturerName',
        'boat.boatModel.model',
        'boat.boatModel.modelYear',
    ]

    const debouncedSearchQuery = useDebounce(apiSearch, 1000)

    const onApiSearch = useCallback((query: any) => {
        setState({
            apiSearch: query,
        })
        if (query !== null) {
            setState({ paginationModel: { ...paginationModel, page: 0 } })
        }
    }, [])

    const getData = async (active) => {
        setState({ loading: true })
        let userRegistrationData: any
        try {
            const userRegistration = await getUsersRegistrationWithLimiter({
                active,
                page: paginationModel?.page + 1,
                limit: 50,
                filter: debouncedSearchQuery
                    ? JSON.stringify(debouncedSearchQuery)
                    : undefined,
            })
            console.log(userRegistration, 'DISPLAY YOURSELF!')
            userRegistrationData = Array.isArray(userRegistration?.usersReg)
                ? userRegistration?.usersReg
                : []
            const total = Number(userRegistration?.total) || 0

            if (
                userRegistrationData?.length === 0 &&
                total === 0 &&
                debouncedSearchQuery !== null
            ) {
                setToastMessage(
                    'No Users found - Please Try another Search',
                    'info'
                )
                setState({
                    apiSearch: null,
                    paginationModel: { page: 0, pageSize: 50 },
                    rowCount: total,
                    loading: false,
                    noResults: true,
                })
                return
            }
            userRegistrationData = columnMapper(
                defaultColumns,
                userRegistration?.usersReg
            ).map((item: any) => ({
                ...item,
                id: item?._id,
                lastEmailSent: item?.userInviteSent?.updatedAt
                    ? new Date(item.userInviteSent.updatedAt)
                    : null,
                emailCount: item?.userInviteSent?.count || 0,
            }))
            console.log('Sample user data:', userRegistration?.usersReg[0])
            setState({
                data: userRegistrationData,
                rowCount: total,
                loading: false,
                noResults: false,
            })
        } catch (err: any) {
            let errorMessage = 'An unknown error occurred'

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error
            } else if (err.message) {
                errorMessage = err.message
            }
            setState({ data: [], rowCount: 0, users: [] })
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const handleSendWelcomeEmail = async () => {
        console.log('selectedUserIds:', selectedUserIds)

        if (!selectedUserIds || (selectedUserIds as any)?.ids?.size === 0) {
            setToastMessage('Please select at least one user.', 'warning')
            return
        }

        try {
            setToastMessage('Sending welcome emails...', 'info')

            let userIdsArray: string[] = []

            if (
                selectedUserIds &&
                typeof selectedUserIds === 'object' &&
                (selectedUserIds as any).ids instanceof Set
            ) {
                // Convert Set to Array
                userIdsArray = Array.from((selectedUserIds as any).ids)
            } else if (Array.isArray(selectedUserIds)) {
                userIdsArray = selectedUserIds
            }

            console.log('Converted userIdsArray:', userIdsArray)

            if (userIdsArray.length === 0) {
                setToastMessage('No valid user IDs selected.', 'warning')
                return
            }

            const response = await sendWelcomeEmails(userIdsArray)

            setState({
                emailStats: {
                    lastSentTimestamp: new Date().toISOString(),
                    totalEmailsSent: response.emailsSent || userIdsArray.length,
                },
            })

            setToastMessage(
                `Welcome email(s) sent successfully to ${
                    response.emailsSent || userIdsArray.length
                } users.`,
                'success'
            )

            await getData(true)
            setState({ selectedUserIds: [] })
        } catch (err) {
            console.error('Email sending error:', err)
            const error = err as any
            setToastMessage(
                error?.response?.data?.message || 'Error sending emails.',
                'error'
            )
        }
    }

    const buttons = [
        {
            buttonName: 'Send Welcome Email',
            buttonIcon: <ContactMailIcon />,
            buttonFunction: handleSendWelcomeEmail,
        },
    ]

    const collectColumnData = () => {
        const users =
            userPreferences?.dataTableColumns?.users?.userRegistration?.columns

        if (Array.isArray(users)) {
            const mapColumns = users.map((item) => ({
                field: item?.field,
                headerName: item?.headerName,
                width: item?.width,
                isVisible: item.isVisible,
                type: item?.type,
            }))

            const format = {
                userRegistration: {
                    columns: mapColumns,
                },
            }

            setState({ ...state, users: format })
        }
    }
    const submitColumnData = async () => {
        try {
            setUser({
                key: 'users',
                value: users,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    useEffect(() => {
        collectColumnData()
        getData(true)
    }, [])

    useEffect(() => {
        if (noResults) {
            return
        } else {
            getData(true)
        }
    }, [paginationModel?.page, debouncedSearchQuery])

    useEffect(() => {
        setState({ paginationModel: { page: 0, pageSize: 50 } })
        getData(true)
    }, [activeManufacturer])

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
            type: col.type,
        }))

        const setDensity = state.density

        const setData = {
            userRegistration: { columns: format, density: setDensity },
        }

        setState({
            users: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            users: {
                userRegistration: {
                    ...userRegistration,
                    density: newDensity,
                },
            },
        })
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <UniversalToolBar buttons={buttons} />
            </Grid>
            <Grid container>
                <UniversalLoadingCircle
                    data={data}
                    reloadFunction={() => getData(true)}
                    customTimeoutMessage={'No Available Users'}
                >
                    <UniversalDataTable
                        data={data}
                        apiDensity={density}
                        apiColumns={users?.userRegistration?.columns}
                        checkboxSelection={true}
                        onRowSelectionModelChange={(selectionModel) =>
                            setState({ selectedUserIds: selectionModel })
                        }
                        savePreferences={() => submitColumnData()}
                        visibleColumns={[]} // adding default columns and removing the array causes it to render the column names in the string
                        getRowData={(tableRow) =>
                            setState({
                                open: true,
                                row: tableRow,
                            })
                        }
                        onUpdatedColumnsChange={handleUpdatedColumnsChange}
                        onDensityChange={handleDensityChange}
                        sx={{ width: 10 }}
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        loading={loading}
                        paginationMode={'server'}
                        pageSizeOptions={[50]}
                        setPaginationModel={(data) => {
                            setState({ paginationModel: data })
                        }}
                        onApiSearch={(text) => {
                            setState({ noResults: false })
                            onApiSearch(text)
                        }}
                    />
                </UniversalLoadingCircle>

                <UniversalModal
                    title={'Register a New User'}
                    open={open}
                    onClose={() => setState({ open: false })}
                    timeStamp={`${
                        row.fullHin
                            ? ` ${
                                  row.updatedBy
                                      ? ` Modified By ${row.updatedBy}`
                                      : ''
                              } ${
                                  row.updatedAt
                                      ? `| Modified Date ${row.updatedAt.substring(
                                            0,
                                            10
                                        )}`
                                      : ''
                              }  `
                            : ` Modified By: ${userEmail} | Modified Date: ${today} `
                    }`}
                    wrapperStyle={{
                        margin: 'auto',
                        width: '90%',
                        marginTop: 10,
                        marginBottom: 5,
                    }}
                    draggable={true}
                    minimizable={true}
                >
                    <UserRegistrationForm
                        row={row}
                        onClose={() => {
                            setState({ open: false })
                            getData(true)
                        }}
                    />
                </UniversalModal>
            </Grid>
        </Grid>
    )
}
